/**
 * What a place's Google `types` say about it: the category each type sits in,
 * and the icon the Place search draws the place with. Mirrors iOS
 * `LocationPlaceType` / `LocationPlaceCategoryType` and
 * `CommonHelpers.getLocationPlaceIconString`; a type iOS doesn't list says
 * nothing here either.
 *
 * Pure: no React, no Google SDK, so it tests in node.
 */

/** iOS `LocationPlaceCategoryType`: Google's own grouping of its place types. */
export type PlaceCategory =
	| 'automotive'
	| 'business'
	| 'culture'
	| 'education'
	| 'entertainmentAndRecreation'
	| 'facilities'
	| 'finance'
	| 'foodAndDrink'
	| 'geographicalAreas'
	| 'government'
	| 'healthAndWellness'
	| 'housing'
	| 'lodging'
	| 'naturalFeatures'
	| 'placesOfWorship'
	| 'services'
	| 'shopping'
	| 'sports'
	| 'transportation'
	/** Types that name no kind of place: an address, a region, "point of interest". */
	| 'additional'

/** Every Google type iOS knows, by the category it sits in — the same lists in the same order. */
export const PLACE_TYPES_BY_CATEGORY: Readonly<Record<PlaceCategory, readonly string[]>> = {
	automotive: [
		'car_dealer', 'car_rental', 'car_repair', 'car_wash', 'electric_vehicle_charging_station',
		'gas_station', 'parking', 'rest_stop',
	],
	business: [
		'corporate_office', 'farm', 'ranch',
	],
	culture: [
		'art_gallery', 'art_studio', 'auditorium', 'cultural_landmark', 'historical_place', 'monument',
		'museum', 'performing_arts_theater', 'sculpture',
	],
	education: [
		'library', 'preschool', 'primary_school', 'school', 'secondary_school', 'university',
	],
	entertainmentAndRecreation: [
		'adventure_sports_center', 'amphitheatre', 'amusement_center', 'amusement_park', 'aquarium',
		'banquet_hall', 'barbecue_area', 'botanical_garden', 'bowling_alley', 'casino', 'childrens_camp',
		'comedy_club', 'community_center', 'concert_hall', 'convention_center', 'cultural_center',
		'cycling_park', 'dance_hall', 'dog_park', 'event_venue', 'ferris_wheel', 'garden', 'hiking_area',
		'historical_landmark', 'internet_cafe', 'karaoke', 'marina', 'movie_rental', 'movie_theater',
		'national_park', 'night_club', 'observation_deck', 'off_roading_area', 'opera_house', 'park',
		'philharmonic_hall', 'picnic_ground', 'planetarium', 'plaza', 'roller_coaster',
		'skateboard_park', 'state_park', 'tourist_attraction', 'video_arcade', 'visitor_center',
		'water_park', 'wedding_venue', 'wildlife_park', 'wildlife_refuge', 'zoo',
	],
	facilities: [
		'public_bath', 'public_bathroom', 'stable',
	],
	finance: [
		'accounting', 'atm', 'bank',
	],
	foodAndDrink: [
		'acai_shop', 'afghani_restaurant', 'african_restaurant', 'american_restaurant',
		'asian_restaurant', 'bagel_shop', 'bakery', 'bar', 'bar_and_grill', 'barbecue_restaurant',
		'brazilian_restaurant', 'breakfast_restaurant', 'brunch_restaurant', 'buffet_restaurant', 'cafe',
		'cafeteria', 'candy_store', 'cat_cafe', 'chinese_restaurant', 'chocolate_factory',
		'chocolate_shop', 'coffee_shop', 'confectionery', 'deli', 'dessert_restaurant', 'dessert_shop',
		'diner', 'dog_cafe', 'donut_shop', 'fast_food_restaurant', 'fine_dining_restaurant',
		'food_court', 'french_restaurant', 'greek_restaurant', 'hamburger_restaurant', 'ice_cream_shop',
		'indian_restaurant', 'indonesian_restaurant', 'italian_restaurant', 'japanese_restaurant',
		'juice_shop', 'korean_restaurant', 'lebanese_restaurant', 'meal_delivery', 'meal_takeaway',
		'mediterranean_restaurant', 'mexican_restaurant', 'middle_eastern_restaurant',
		'pizza_restaurant', 'pub', 'ramen_restaurant', 'restaurant', 'sandwich_shop',
		'seafood_restaurant', 'spanish_restaurant', 'steak_house', 'sushi_restaurant', 'tea_house',
		'thai_restaurant', 'turkish_restaurant', 'vegan_restaurant', 'vegetarian_restaurant',
		'vietnamese_restaurant', 'wine_bar',
	],
	geographicalAreas: [
		'administrative_area_level_1', 'administrative_area_level_2', 'country', 'locality',
		'postal_code', 'school_district',
	],
	government: [
		'city_hall', 'courthouse', 'embassy', 'fire_station', 'government_office',
		'local_government_office', 'neighborhood_police_station', 'police', 'post_office',
	],
	healthAndWellness: [
		'chiropractor', 'dental_clinic', 'dentist', 'doctor', 'drugstore', 'hospital', 'massage',
		'medical_lab', 'pharmacy', 'physiotherapist', 'sauna', 'skin_care_clinic', 'spa',
		'tanning_studio', 'wellness_center', 'yoga_studio',
	],
	housing: [
		'apartment_building', 'apartment_complex', 'condominium_complex', 'housing_complex',
	],
	lodging: [
		'bed_and_breakfast', 'budget_japanese_inn', 'campground', 'camping_cabin', 'cottage',
		'extended_stay_hotel', 'farmstay', 'guest_house', 'hostel', 'hotel', 'inn', 'japanese_inn',
		'lodging', 'mobile_home_park', 'motel', 'private_guest_room', 'resort_hotel', 'rv_park',
	],
	naturalFeatures: [
		'beach',
	],
	placesOfWorship: [
		'church', 'hindu_temple', 'mosque', 'synagogue',
	],
	services: [
		'astrologer', 'barber_shop', 'beautician', 'beauty_salon', 'body_art_service',
		'catering_service', 'cemetery', 'child_care_agency', 'consultant', 'courier_service',
		'electrician', 'florist', 'food_delivery', 'foot_care', 'funeral_home', 'hair_care',
		'hair_salon', 'insurance_agency', 'laundry', 'lawyer', 'locksmith', 'makeup_artist',
		'moving_company', 'nail_salon', 'painter', 'plumber', 'psychic', 'real_estate_agency',
		'roofing_contractor', 'storage', 'summer_camp_organizer', 'tailor',
		'telecommunications_service_provider', 'tour_agency', 'tourist_information_center',
		'travel_agency', 'veterinary_care',
	],
	shopping: [
		'asian_grocery_store', 'auto_parts_store', 'bicycle_store', 'book_store', 'butcher_shop',
		'cell_phone_store', 'clothing_store', 'convenience_store', 'department_store', 'discount_store',
		'electronics_store', 'food_store', 'furniture_store', 'gift_shop', 'grocery_store',
		'hardware_store', 'home_goods_store', 'home_improvement_store', 'jewelry_store', 'liquor_store',
		'market', 'pet_store', 'shoe_store', 'shopping_mall', 'sporting_goods_store', 'store',
		'supermarket', 'warehouse_store', 'wholesaler',
	],
	sports: [
		'arena', 'athletic_field', 'fishing_charter', 'fishing_pond', 'fitness_center', 'golf_course',
		'gym', 'ice_skating_rink', 'playground', 'ski_resort', 'sports_activity_location', 'sports_club',
		'sports_coaching', 'sports_complex', 'stadium', 'swimming_pool',
	],
	transportation: [
		'airport', 'airstrip', 'bus_station', 'bus_stop', 'ferry_terminal', 'heliport',
		'international_airport', 'light_rail_station', 'park_and_ride', 'subway_station', 'taxi_stand',
		'train_station', 'transit_depot', 'transit_station', 'truck_stop',
	],
	additional: [
		'administrative_area_level_3', 'administrative_area_level_4', 'administrative_area_level_5',
		'administrative_area_level_6', 'administrative_area_level_7', 'archipelago', 'colloquial_area',
		'continent', 'establishment', 'finance', 'food', 'general_contractor', 'geocode', 'health',
		'intersection', 'landmark', 'natural_feature', 'neighborhood', 'place_of_worship', 'plus_code',
		'point_of_interest', 'political', 'postal_code_prefix', 'postal_code_suffix', 'postal_town',
		'premise', 'route', 'street_address', 'sublocality', 'sublocality_level_1',
		'sublocality_level_2', 'sublocality_level_3', 'sublocality_level_4', 'sublocality_level_5',
		'subpremise', 'town_square',
	],
}

const CATEGORY_BY_TYPE = new Map<string, PlaceCategory>(
	(Object.entries(PLACE_TYPES_BY_CATEGORY) as [PlaceCategory, readonly string[]][]).flatMap(([category, types]) =>
		types.map(type => [type, category] as const),
	),
)

/** The category a Google type sits in, or null for a type iOS doesn't list. */
export const placeCategoryOf = (type: string): PlaceCategory | null => CATEGORY_BY_TYPE.get(type) ?? null

/** The glyph a place is drawn with; `@components/PlaceTypeIcon` draws each one. */
export type PlaceIcon =
	// A type's own glyph
	| 'airport'
	| 'bakery'
	| 'beer'
	| 'boat'
	| 'bus'
	| 'cafe'
	| 'comedy'
	| 'cycling'
	| 'dessert'
	| 'fishing'
	| 'game'
	| 'garden'
	| 'golf'
	| 'gym'
	| 'haircut'
	| 'hiking'
	| 'music'
	| 'park'
	| 'pet'
	| 'ski'
	| 'spa'
	| 'subway'
	| 'swimming'
	// A category's glyph
	| 'automotive'
	| 'business'
	| 'culture'
	| 'education'
	| 'entertainment'
	| 'facilities'
	| 'finance'
	| 'food'
	| 'geoArea'
	| 'government'
	| 'health'
	| 'housing'
	| 'lodging'
	| 'naturalFeatures'
	| 'services'
	| 'shopping'
	| 'sports'
	| 'transportation'
	| 'worship'

const CATEGORY_ICON: Readonly<Record<PlaceCategory, PlaceIcon>> = {
	automotive: 'automotive',
	business: 'business',
	culture: 'culture',
	education: 'education',
	entertainmentAndRecreation: 'entertainment',
	facilities: 'facilities',
	finance: 'finance',
	foodAndDrink: 'food',
	geographicalAreas: 'geoArea',
	government: 'government',
	healthAndWellness: 'health',
	housing: 'housing',
	lodging: 'lodging',
	naturalFeatures: 'naturalFeatures',
	placesOfWorship: 'worship',
	services: 'services',
	shopping: 'shopping',
	sports: 'sports',
	transportation: 'transportation',
	additional: 'geoArea',
}

/** Types with a glyph of their own (iOS `LocationPlaceType.getIconString`); every other type takes its category's. */
const TYPE_ICON: Readonly<Record<string, PlaceIcon>> = {
	cycling_park: 'cycling',
	// iOS names a dog icon it has no image for; the paw is the one it ships.
	dog_park: 'pet',
	dog_cafe: 'pet',
	comedy_club: 'comedy',
	movie_theater: 'comedy',
	garden: 'garden',
	botanical_garden: 'garden',
	park: 'park',
	national_park: 'park',
	wildlife_park: 'park',
	karaoke: 'music',
	hiking_area: 'hiking',
	video_arcade: 'game',
	cafe: 'cafe',
	tea_house: 'cafe',
	bakery: 'bakery',
	dessert_shop: 'dessert',
	dessert_restaurant: 'dessert',
	ice_cream_shop: 'dessert',
	bar: 'beer',
	liquor_store: 'beer',
	spa: 'spa',
	massage: 'spa',
	hair_care: 'haircut',
	hair_salon: 'haircut',
	gym: 'gym',
	fitness_center: 'gym',
	fishing_pond: 'fishing',
	fishing_charter: 'fishing',
	golf_course: 'golf',
	swimming_pool: 'swimming',
	ski_resort: 'ski',
	airport: 'airport',
	heliport: 'airport',
	international_airport: 'airport',
	bus_stop: 'bus',
	bus_station: 'bus',
	ferry_terminal: 'boat',
	food: 'food',
	food_court: 'food',
	subway_station: 'subway',
	light_rail_station: 'subway',
}

/**
 * The types that decide a place's icon ahead of the rest, most telling first —
 * so a restaurant with a bar reads as a bar whichever type Google lists first.
 */
const ICON_PRIORITY: readonly string[] = [
	'food', 'food_court', 'cafe', 'tea_house', 'bar', 'liquor_store', 'bakery', 'dessert_shop',
	'dessert_restaurant', 'ice_cream_shop', 'spa', 'massage', 'hair_care', 'hair_salon', 'gym',
	'fitness_center', 'cycling_park', 'dog_park', 'dog_cafe', 'comedy_club', 'movie_theater', 'garden',
	'botanical_garden', 'park', 'national_park', 'wildlife_park', 'karaoke', 'hiking_area', 'video_arcade',
	'fishing_pond', 'fishing_charter', 'golf_course', 'swimming_pool', 'ski_resort', 'airport', 'heliport',
	'international_airport', 'bus_stop', 'bus_station', 'subway_station', 'light_rail_station',
]

const iconOfType = (type: string): PlaceIcon | null => {
	const category = placeCategoryOf(type)
	if (!category) return null
	return TYPE_ICON[type] ?? CATEGORY_ICON[category]
}

/**
 * The icon for a place with these Google types: the first of iOS's priority
 * types it has, else its first known type's, else the pin — which a typed-in
 * place, with no types at all, always gets.
 */
export const placeIconOf = (types: readonly string[] | null | undefined): PlaceIcon => {
	const known = (types ?? []).filter(type => placeCategoryOf(type) !== null)
	const decisive = ICON_PRIORITY.find(type => known.includes(type)) ?? known[0]
	return (decisive && iconOfType(decisive)) || 'geoArea'
}
